// Globals
import { Trans, useTranslation } from 'react-i18next';

// Models
import { IFile } from '@/models/file.model';
import { AppraiserStepProgress } from '@/models/propertyProgress.model';
import { StepType } from '@/models/step.model';

// Store
import { useAppState } from '@/store';
import { selectPropertyOwner, selectStepsProgresses } from '../../feature';

// Modules
import { UploadCard } from '@/modules/Upload';

// Components
import { ResetStepButton, StepCard, StyledLabel, StyledRow, StyledText, StyledTime } from '../../components';
import { StyledDateInfo, StyledInfo, StyledList } from './AppraiserProgressContainerStyles';

// Helpers
import { DateFormats } from '@/constants/dates';
import { renderCurrency, renderDate } from '@/utils';
import { finalStatuses, getAppraiserStatus, getStepUploader } from '../../helpers';

type TAppraiserProgressContainerProps = {
  hideResetButton?: boolean;
};

const AppraiserProgressContainer = ({ hideResetButton = false }: TAppraiserProgressContainerProps) => {
  const { t } = useTranslation('property');
  const stepsProgresses = useAppState(selectStepsProgresses);
  const propertyOwner = useAppState(selectPropertyOwner);
  const appraiseProgress = stepsProgresses?.[StepType.APPRAISER] as AppraiserStepProgress;

  const handlePreview = (file: IFile) => window.open(file.link, '_blank');

  if (!appraiseProgress) return null;

  const user = getStepUploader(appraiseProgress);

  return (
    <StepCard title={t('step_appraiser')} status={getAppraiserStatus(appraiseProgress)}>
      <StyledRow>
        <div>
          {user && (
            <>
              <StyledLabel>{t('label_who_uploaded')}</StyledLabel>
              <StyledText>
                {t('property_upload_by', {
                  name: propertyOwner?.full_name,
                  context: user,
                })}
              </StyledText>
            </>
          )}
        </div>
        {!hideResetButton && finalStatuses.includes(appraiseProgress.status) && (
          <ResetStepButton
            propertyId={appraiseProgress?.property_id}
            stepName={StepType.APPRAISER}
            shouldResetStepData
          />
        )}
      </StyledRow>
      <>
        {!!appraiseProgress?.appraisal?.files?.length && (
          <StyledList>
            {appraiseProgress?.appraisal?.files?.map((file) => (
              <UploadCard
                key={file.id}
                file={{
                  link: file.file_link,
                  name: file.file_name,
                  id: file.id,
                }}
                onPreview={handlePreview}
                additionalInfo={
                  <StyledTime>
                    <Trans
                      i18nKey="property_upload_at"
                      ns="property"
                      values={{ date: renderDate(file.uploadedAt, DateFormats.WITH_DOTS) }}
                      context={file.uploadedBy}
                      components={{
                        span: <span />,
                      }}
                    />
                  </StyledTime>
                }
              />
            ))}
          </StyledList>
        )}
        {(appraiseProgress?.appraisal?.condition_report_created_at ||
          appraiseProgress?.appraisal?.rate_report_created_at) && (
          <StyledInfo>
            {appraiseProgress?.appraisal?.condition_report_created_at && (
              <StyledDateInfo>
                {t('label_condition_report', {
                  date: renderDate(appraiseProgress?.appraisal?.condition_report_created_at, DateFormats.WITH_DOTS),
                })}
              </StyledDateInfo>
            )}
            {appraiseProgress?.appraisal?.rate_report_created_at && (
              <StyledDateInfo>
                {t('label_rate', {
                  date: renderDate(appraiseProgress?.appraisal?.rate_report_created_at, DateFormats.WITH_DOTS),
                })}
              </StyledDateInfo>
            )}
          </StyledInfo>
        )}
        {appraiseProgress?.appraisal?.price && (
          <StyledInfo>
            <StyledDateInfo>
              {t('label_price', {
                price: renderCurrency(appraiseProgress?.appraisal?.price),
              })}
            </StyledDateInfo>
          </StyledInfo>
        )}
      </>
    </StepCard>
  );
};

export default AppraiserProgressContainer;
